'use client';

import { Link as LinkIcon, Mic, Square, Upload } from 'lucide-react';
import { useTranslations } from 'next-intl';
import * as React from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { PlanUpsell, useCapability } from '@/features/billing';
import { Input } from '@/components/ui/Input';
import { getConfig } from '@/lib/config';
import { cn } from '@/lib/utils';
import { elapsedSecs } from './audioDuration';
import { useRecordingsStore } from './recordingsStore';
import { RECORDING_ACCEPTED_TYPES, validateFile } from './validateFile';

// Live timer display: m:ss, or h:mm:ss past an hour.
function formatClock(totalSec: number): string {
  const s = Math.max(0, Math.floor(totalSec));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return h > 0
    ? `${h}:${m.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`
    : `${m}:${sec.toString().padStart(2, '0')}`;
}

// Human "max length" label: "4h", "1h 30m", "45m".
function formatMaxLabel(totalSec: number): string {
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  if (h > 0 && m > 0) return `${h}h ${m}m`;
  if (h > 0) return `${h}h`;
  return `${m}m`;
}

// Max audio length the ASR pipeline accepts (mirrors backend cap). Defensive:
// falls back to 4h if config isn't ready (SSR / tests without a config mock).
function maxRecordingSecs(): number {
  try {
    const v = getConfig().recordingsMaxDurationSecs;
    if (typeof v === 'number' && v > 0) return v;
  } catch {
    /* config not ready — use the documented default */
  }
  return 14400;
}

type MicState = 'idle' | 'requesting' | 'recording' | 'error';

interface RecordingUploadProps {
  className?: string;
}

// Wall-clock cap on getUserMedia. Android Chrome can hang here indefinitely
// when site permission is "ask every time" and the prompt is dismissed, or
// when a previous session left the mic in a half-released state. Without a
// timeout the button stays disabled at "Requesting…" forever.
const GET_USER_MEDIA_TIMEOUT_MS = 15_000;

// MediaRecorder timeslice. Periodic dataavailable events ensure we collect
// chunks throughout recording rather than relying on a single final flush.
// On Android Chrome the final-flush path is fragile when tracks race with
// MediaRecorder finalization — chunks can be lost. 1000 ms matches the
// industry standard for chat-style voice apps.
const RECORDER_TIMESLICE_MS = 1000;

// Threshold (linear amplitude 0..1) below which we treat the input as silent
// and surface a friendly toast instead of uploading garbage that whisper will
// hallucinate "Thank you." on. -42 dBFS ≈ 0.008 — well above room noise on a
// working mic, well below any actual speech.
const SILENCE_RMS_THRESHOLD = 0.008;

// Minimal Wake Lock typings — the DOM lib in this TS config doesn't ship them
// and we only need request/release/released. Feature-detected at call time.
interface WakeLockSentinelLike {
  readonly released: boolean;
  release: () => Promise<void>;
}
interface WakeLockLike {
  request: (type: 'screen') => Promise<WakeLockSentinelLike>;
}
function getWakeLock(): WakeLockLike | undefined {
  if (typeof navigator === 'undefined') return undefined;
  return (navigator as Navigator & { wakeLock?: WakeLockLike }).wakeLock;
}

// How long a mic track must stay muted before we treat it as a real
// interruption (screen lock) rather than a transient blip (a notification
// briefly grabbing the mic). Below this, an unmute cancels the stop.
const MUTE_GRACE_MS = 2000;

function withTimeout<T>(p: Promise<T>, ms: number, msg: string): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(
      () => reject(new DOMException(msg, 'TimeoutError')),
      ms,
    );
    p.then(
      (v) => {
        clearTimeout(timer);
        resolve(v);
      },
      (e) => {
        clearTimeout(timer);
        reject(e);
      },
    );
  });
}

function RecordingUpload({ className }: RecordingUploadProps) {
  const t = useTranslations('recordings');
  const upload = useRecordingsStore((s) => s.actions.upload);
  const uploadFromMic = useRecordingsStore((s) => s.actions.uploadFromMic);
  const uploadFromURL = useRecordingsStore((s) => s.actions.uploadFromURL);
  // BILL-20f: below Standard the server refuses upload / mic capture / URL
  // import (241). Only the idle state locks — a capture in progress can always
  // be stopped (D10); its upload then meets the store's 241 hold. `unknown`
  // never locks: the server stays the boundary.
  const asr = useCapability('recordings_asr');
  const asrLocked = asr.entitlement === 'locked';

  const [isDragOver, setIsDragOver] = React.useState(false);
  const [url, setUrl] = React.useState('');
  const [micState, setMicState] = React.useState<MicState>('idle');
  const [micError, setMicError] = React.useState<string | null>(null);
  // Live recording clock. Driven off a wall-clock start timestamp (robust to
  // tab throttling) ticked once a second while recording.
  const [recordingElapsed, setRecordingElapsed] = React.useState(0);
  const recordingStartedAtRef = React.useRef<number | null>(null);

  const inputRef = React.useRef<HTMLInputElement>(null);
  const recorderRef = React.useRef<MediaRecorder | null>(null);
  const streamRef = React.useRef<MediaStream | null>(null);
  // Web Audio plumbing for live RMS sampling. Held in refs so cleanup can
  // tear them down regardless of which path stops the recording.
  const audioCtxRef = React.useRef<AudioContext | null>(null);
  const rmsSamplesRef = React.useRef<number[]>([]);
  const rmsRafRef = React.useRef<number | null>(null);
  // Screen Wake Lock held for the duration of a recording (prevents the
  // mobile screen-lock that suspends the mic). Plus a debounce timer for the
  // track-mute fallback. Both torn down in stopMic.
  const wakeLockRef = React.useRef<WakeLockSentinelLike | null>(null);
  const muteTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  // ---- File / drop ----

  const handleFile = React.useCallback(
    (file: File) => {
      const err = validateFile(file);
      if (err) {
        toast.error(err);
        return;
      }
      void upload(file);
    },
    [upload],
  );

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    // preventDefault first even when locked, or the browser opens the file.
    e.preventDefault();
    if (asrLocked) return;
    setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const onDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (asrLocked) return;
    setIsDragOver(true);
  };

  const onDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const onPickerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) handleFile(f);
    e.target.value = '';
  };

  // ---- URL import ----

  const onURLSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (asrLocked) return;
    const trimmed = url.trim();
    if (!trimmed) return;
    void uploadFromURL(trimmed);
    setUrl('');
  };

  // ---- Microphone ----

  // Synchronously release any mic-side resources. Safe to call multiple times.
  // Tracks are released *immediately* — the alternative (waiting for the MR
  // 'stop' event before releasing) means a fast Stop→Record cycle can collide
  // with the previous stream still holding the device, which on Android
  // surfaces as a hung getUserMedia. mr.start(1000) means we already have
  // periodic chunks, so losing the final ~1 s tail to a forced track stop is
  // an acceptable tradeoff vs. a hung mic.
  const acquireWakeLock = React.useCallback(async () => {
    const wl = getWakeLock();
    if (!wl || wakeLockRef.current) return;
    try {
      wakeLockRef.current = await wl.request('screen');
    } catch {
      // Rejects if the page isn't visible/active, or the browser refuses.
      // Non-fatal: the track-mute fallback still catches a real interruption.
    }
  }, []);

  const releaseWakeLock = React.useCallback(() => {
    const sentinel = wakeLockRef.current;
    wakeLockRef.current = null;
    if (sentinel && !sentinel.released) {
      void sentinel.release().catch(() => {
        /* already released by the UA on hide/navigation */
      });
    }
  }, []);

  const teardownAudioGraph = React.useCallback(() => {
    if (rmsRafRef.current !== null) {
      cancelAnimationFrame(rmsRafRef.current);
      rmsRafRef.current = null;
    }
    const ctx = audioCtxRef.current;
    if (ctx && ctx.state !== 'closed') {
      void ctx.close().catch(() => {
        /* already closing */
      });
    }
    audioCtxRef.current = null;
  }, []);

  const stopMic = React.useCallback(() => {
    if (muteTimerRef.current) {
      clearTimeout(muteTimerRef.current);
      muteTimerRef.current = null;
    }
    const rec = recorderRef.current;
    if (rec && rec.state !== 'inactive') {
      try {
        rec.stop();
      } catch {
        /* already stopped */
      }
    }
    const stream = streamRef.current;
    if (stream) {
      for (const t of stream.getTracks()) t.stop();
    }
    recorderRef.current = null;
    streamRef.current = null;
    releaseWakeLock();
    teardownAudioGraph();
  }, [teardownAudioGraph, releaseWakeLock]);

  const installRmsMeter = React.useCallback((stream: MediaStream) => {
    rmsSamplesRef.current = [];
    try {
      const Ctor: typeof AudioContext | undefined =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext?: typeof AudioContext })
          .webkitAudioContext;
      if (!Ctor) return;
      const ctx = new Ctor();
      audioCtxRef.current = ctx;
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 2048;
      source.connect(analyser);
      const buf = new Float32Array(analyser.fftSize);
      const tick = () => {
        if (audioCtxRef.current !== ctx) return;
        analyser.getFloatTimeDomainData(buf);
        let sumSq = 0;
        for (let i = 0; i < buf.length; i++) sumSq += buf[i]! * buf[i]!;
        const rms = Math.sqrt(sumSq / buf.length);
        rmsSamplesRef.current.push(rms);
        rmsRafRef.current = requestAnimationFrame(tick);
      };
      rmsRafRef.current = requestAnimationFrame(tick);
    } catch {
      // AudioContext can fail on iOS Safari prior to user gesture, or on
      // browsers where the API is locked. We tolerate the loss of the
      // silence check — it's defense-in-depth, not load-bearing.
    }
  }, []);

  const wasInputSilent = React.useCallback((): boolean => {
    const samples = rmsSamplesRef.current;
    if (samples.length < 5) return false; // not enough data to judge
    // Use a 90th-percentile-style measure rather than mean so brief loud
    // bursts (chair scrape, finger tap) don't mask an otherwise-silent take.
    const sorted = [...samples].sort((a, b) => a - b);
    const idx = Math.min(sorted.length - 1, Math.floor(sorted.length * 0.9));
    const p90 = sorted[idx] ?? 0;
    return p90 < SILENCE_RMS_THRESHOLD;
  }, []);

  const onMicClick = async () => {
    if (micState === 'recording') {
      // Capture silence verdict BEFORE we tear down the audio graph.
      const silent = wasInputSilent();
      stopMic();
      setMicState('idle');
      if (silent) {
        toast.error(t('recordingUpload.noAudioCaptured'), {
          description: t('recordingUpload.noAudioCapturedDesc'),
        });
      }
      return;
    }
    if (asrLocked) return;
    // Belt-and-suspenders: ensure no stale stream is held from a previous
    // attempt before requesting a new one.
    stopMic();

    setMicState('requesting');
    setMicError(null);
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new DOMException(
          'Microphone unavailable in this browser',
          'NotSupportedError',
        );
      }
      // Explicit audio constraints rather than a bare `{ audio: true }`. On
      // Firefox-Android specifically, the bare form has yielded silent tracks
      // depending on OS routing; naming the standard processing flags wakes
      // the audio graph reliably. Desktop browsers ignore unknowns gracefully.
      const stream = await withTimeout(
        navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
        }),
        GET_USER_MEDIA_TIMEOUT_MS,
        'Microphone request timed out — try reloading the page, granting site permission, or restarting the browser',
      );
      streamRef.current = stream;

      // Sanity-check the acquired track. A track in 'ended' or muted state
      // at acquire time on Android Chrome usually means the OS-level mic
      // toggle is off or another app holds the mic.
      const audioTrack = stream.getAudioTracks()[0];
      if (!audioTrack || audioTrack.readyState !== 'live') {
        throw new DOMException(
          'Microphone track is not live — the OS may be blocking audio input',
          'NotReadableError',
        );
      }
      if (audioTrack.muted) {
        // We continue but warn loudly. The user may not realize their
        // Android system mute is on.
        toast.warning(t('recordingUpload.micMuted'), {
          description: t('recordingUpload.micMutedDesc'),
        });
      }

      // Pick a mimeType the backend's sniffer knows AND that the current
      // browser actually encodes reliably. Probe order matters:
      //  - Firefox (desktop AND android): the native, fully-tested path is
      //    Opus-in-Ogg. Firefox-Android's audio/webm encoder has a long
      //    history of returning isTypeSupported=true but producing silent
      //    or malformed containers — putting Ogg first avoids that trap.
      //  - Chromium: audio/webm;codecs=opus is the canonical choice.
      //  - Safari: only audio/mp4 is supported.
      // Without explicit codec hints, plain "audio/webm" / "audio/ogg" let
      // the browser pick its own codec, which on some Android builds is a
      // codec the backend's magic-byte sniffer rejects.
      const candidates = [
        'audio/ogg;codecs=opus',
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/mp4;codecs=mp4a.40.2',
        'audio/mp4',
      ];
      let mimeType: string | undefined;
      if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported) {
        for (const c of candidates) {
          if (MediaRecorder.isTypeSupported(c)) {
            mimeType = c;
            break;
          }
        }
      }
      // If nothing matched, let MediaRecorder pick its default — better than
      // throwing here, since the backend re-encodes via ffmpeg anyway.
      const recorderOpts: MediaRecorderOptions = mimeType ? { mimeType } : {};
      const mr = new MediaRecorder(stream, recorderOpts);
      // Closure-local chunks (per recorder instance) so two overlapping
      // MediaRecorder lifecycles — e.g. a stale stop event arriving after
      // the user has started a new recording — can't contaminate each
      // other's data.
      const chunks: Blob[] = [];

      mr.addEventListener('dataavailable', (ev: BlobEvent) => {
        if (ev.data && ev.data.size > 0) chunks.push(ev.data);
      });
      mr.addEventListener('error', (ev: Event) => {
        // MediaRecorder error events have varied shapes across browsers.
        // Both `(ev as ErrorEvent).error` and `(ev as { error: DOMException }).error`
        // are valid; some browsers attach the error to the recorder itself.
        const e =
          (ev as { error?: unknown }).error ||
          (ev as ErrorEvent).error ||
          new Error(t('recordingUpload.recorderErrorFallback'));
        const msg = e instanceof Error ? e.message : String(e);
        setMicError(msg);
        setMicState('error');
        toast.error(t('recordingUpload.recorderError', { message: msg }));
        stopMic();
      });
      mr.addEventListener('stop', () => {
        const blob = new Blob(chunks, {
          type: mr.mimeType || 'audio/webm',
        });
        if (blob.size === 0) {
          toast.error(t('recordingUpload.noAudioProduced'), {
            description: t('recordingUpload.noAudioProducedDesc'),
          });
          return;
        }
        // BILL-05: the recorder's timer is the duration hint — MediaRecorder
        // WebM carries no duration header for the server to read.
        void uploadFromMic(blob, undefined, elapsedSecs(recordingStartedAtRef.current, Date.now()));
      });

      // Mid-recording mic interruption (the "silent after exactly 2:00" bug):
      // on mobile the screen locking suspends the track — MediaRecorder keeps
      // emitting silent chunks, so the timer runs on but the audio is dead.
      // The track fires `mute` (and eventually `ended`) when that happens.
      // We stop on a SUSTAINED mute (debounced, so a transient blip from a
      // notification doesn't end the take) and keep the good audio captured so
      // far. The Wake Lock above should prevent the lock outright; this is the
      // fallback for when it's unsupported or denied.
      const onTrackMute = () => {
        if (muteTimerRef.current) clearTimeout(muteTimerRef.current);
        muteTimerRef.current = setTimeout(() => {
          muteTimerRef.current = null;
          // Only act if still recording AND still muted.
          if (recorderRef.current && audioTrack.muted) {
            toast.warning(t('recordingUpload.micInterrupted'), {
              description: t('recordingUpload.micInterruptedDesc'),
            });
            stopMic();
            setMicState('idle');
          }
        }, MUTE_GRACE_MS);
      };
      const onTrackUnmute = () => {
        if (muteTimerRef.current) {
          clearTimeout(muteTimerRef.current);
          muteTimerRef.current = null;
        }
      };
      const onTrackEnded = () => {
        if (!recorderRef.current) return;
        toast.warning(t('recordingUpload.micUnavailable'), {
          description: t('recordingUpload.micUnavailableDesc'),
        });
        stopMic();
        setMicState('idle');
      };
      audioTrack.addEventListener('mute', onTrackMute);
      audioTrack.addEventListener('unmute', onTrackUnmute);
      audioTrack.addEventListener('ended', onTrackEnded);

      recorderRef.current = mr;
      installRmsMeter(stream);
      mr.start(RECORDER_TIMESLICE_MS);
      recordingStartedAtRef.current = Date.now();
      setRecordingElapsed(0);
      setMicState('recording');
      // Keep the screen awake so Android doesn't auto-lock and suspend the mic.
      void acquireWakeLock();
    } catch (err) {
      // Always release any partially-acquired stream on failure.
      if (streamRef.current) {
        for (const t of streamRef.current.getTracks()) t.stop();
      }
      streamRef.current = null;
      recorderRef.current = null;
      teardownAudioGraph();

      // Browser-local MediaRecorder/getUserMedia errors — map the known cases to
      // warm copy; never surface a raw exception string (R7 tone).
      const name = err instanceof DOMException ? err.name : '';
      const msg =
        name === 'NotAllowedError'
          ? t('recordingUpload.errNotAllowed')
          : name === 'NotFoundError'
            ? t('recordingUpload.errNotFound')
            : name === 'NotReadableError'
              ? t('recordingUpload.errNotReadable')
              : name === 'TimeoutError'
                ? t('recordingUpload.errTimeout')
                : t('recordingUpload.errGeneric');
      setMicError(msg);
      setMicState('error');
      toast.error(msg);
    }
  };

  React.useEffect(() => () => stopMic(), [stopMic]);

  // The UA auto-releases a screen wake lock when the tab is hidden, so when we
  // come back to the foreground mid-recording we must re-request it. Only
  // active while recording.
  React.useEffect(() => {
    if (micState !== 'recording') return undefined;
    const onVisibility = () => {
      if (document.visibilityState === 'visible') void acquireWakeLock();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [micState, acquireWakeLock]);

  // Live recording clock + hard cap. Ticks once a second off the wall-clock
  // start; when it reaches the server's max audio length we auto-stop and warn
  // so a long capture never fails server-side for being over-length.
  React.useEffect(() => {
    if (micState !== 'recording') {
      setRecordingElapsed(0);
      return undefined;
    }
    const maxSecs = maxRecordingSecs();
    const tick = () => {
      const startedAt = recordingStartedAtRef.current;
      if (startedAt == null) return;
      const secs = Math.floor((Date.now() - startedAt) / 1000);
      setRecordingElapsed(secs);
      if (maxSecs > 0 && secs >= maxSecs) {
        toast.warning(t('recordingUpload.maxLengthReached'), {
          description: t('recordingUpload.maxLengthReachedDesc', {
            max: formatMaxLabel(maxSecs),
          }),
        });
        stopMic();
        setMicState('idle');
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [micState, stopMic]);

  // ---- Render ----

  return (
    <div className={cn('space-y-4', className)}>
      {asrLocked && (
        <div id="recordings-plan-upsell" role="status">
          <PlanUpsell
            variant="inline"
            feature="recordings_asr"
            requiredTier={asr.requiredTier || undefined}
            // New tab: a held capture lives in this tab's memory, and checkout redirects the tab.
            newTab
          />
        </div>
      )}
      {/* Drop zone + picker */}
      <div
        data-testid="recordings-dropzone"
        role="button"
        tabIndex={0}
        aria-disabled={asrLocked ? true : undefined}
        aria-describedby={asrLocked ? 'recordings-plan-upsell' : undefined}
        onClick={() => {
          if (asrLocked) return;
          inputRef.current?.click();
        }}
        onKeyDown={(e) => {
          if (asrLocked) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            inputRef.current?.click();
          }
        }}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        className={cn(
          'flex w-full flex-col items-center justify-center gap-2 rounded-md border border-dashed border-border p-8 transition-colors duration-150',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          // Locked: clicks are ignored, so no clickable affordance (audit-ui R3).
          asrLocked ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:border-accent hover:bg-hover',
          isDragOver && 'border-accent bg-hover',
        )}
      >
        <Upload className="h-8 w-8 text-muted-foreground" />
        <span className="text-sm text-muted-foreground">
          {t('recordingUpload.dropzone')}
        </span>
        <span className="text-xs text-muted-foreground">
          {t('recordingUpload.acceptedFormats')}
        </span>
        <input
          ref={inputRef}
          type="file"
          accept={RECORDING_ACCEPTED_TYPES}
          className="hidden"
          onChange={onPickerChange}
        />
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        {/* Mic */}
        <div className="rounded-md border border-border p-4">
          <p className="font-serif text-sm font-medium text-foreground">
            {t('recordingUpload.recordAudio')}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            {t('recordingUpload.micHelp', { max: formatMaxLabel(maxRecordingSecs()) })}
          </p>
          <div className="mt-3 flex items-center gap-3">
            <Button
              type="button"
              variant={micState === 'recording' ? 'destructive' : 'primary'}
              size="sm"
              onClick={onMicClick}
              // Never disable Stop (D10): only the idle Record locks.
              disabled={micState === 'requesting' || (asrLocked && micState !== 'recording')}
              aria-pressed={micState === 'recording'}
            >
              {micState === 'recording' ? (
                <>
                  <Square className="h-3.5 w-3.5 mr-1.5" />
                  {t('recordingUpload.stop')}
                </>
              ) : (
                <>
                  <Mic className="h-3.5 w-3.5 mr-1.5" />
                  {micState === 'requesting'
                    ? t('recordingUpload.requesting')
                    : t('recordingUpload.record')}
                </>
              )}
            </Button>
            {micState === 'recording' && (
              <span
                className="flex items-center gap-1.5 text-sm tabular-nums text-foreground"
                aria-label={t('recordingUpload.recordingTime', {
                  elapsed: formatClock(recordingElapsed),
                  max: formatClock(maxRecordingSecs()),
                })}
              >
                <span
                  className="h-2 w-2 shrink-0 rounded-full bg-destructive animate-pulse"
                  aria-hidden
                />
                <span>{formatClock(recordingElapsed)}</span>
                <span className="text-muted-foreground">/ {formatClock(maxRecordingSecs())}</span>
              </span>
            )}
          </div>
          {micError && (
            <p className="mt-2 text-xs text-destructive" role="alert">
              {micError}
            </p>
          )}
        </div>

        {/* URL */}
        <form className="rounded-md border border-border p-4" onSubmit={onURLSubmit}>
          <p className="font-serif text-sm font-medium text-foreground">
            {t('recordingUpload.fromUrl')}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            {t('recordingUpload.urlHelp')}
          </p>
          <div className="mt-3 flex gap-2">
            <Input
              type="url"
              inputMode="url"
              placeholder={t('recordingUpload.urlPlaceholder')}
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              aria-label={t('recordingUpload.urlAriaLabel')}
              className="flex-1"
            />
            <Button type="submit" size="sm" disabled={!url.trim() || asrLocked}>
              <LinkIcon className="h-3.5 w-3.5 mr-1.5" />
              {t('recordingUpload.import')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export { RecordingUpload };
